import React, { useState, useEffect, useRef } from 'react'
import { X, Mic, Square, Play, Send } from 'lucide-react'

interface AudioRecordingModalProps {
  isOpen: boolean
  onClose: () => void
  onSend: (audioBlob: Blob) => void
}

const AudioRecordingModal: React.FC<AudioRecordingModalProps> = ({ isOpen, onClose, onSend }) => {
  const [isRecording, setIsRecording] = useState(false)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null)
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null)
  const [dataArray, setDataArray] = useState<Uint8Array | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    if (isOpen) {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)()
      const analyserNode = audioCtx.createAnalyser()
      analyserNode.fftSize = 256
      const bufferLength = analyserNode.frequencyBinCount
      const dataArray = new Uint8Array(bufferLength)

      setAudioContext(audioCtx)
      setAnalyser(analyserNode)
      setDataArray(dataArray)
    } else {
      setIsRecording(false)
      setAudioBlob(null)
      setIsPlaying(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (isRecording) {
      drawWaveform()
    }
  }, [isRecording])

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (audioContext && analyser) {
        const source = audioContext.createMediaStreamSource(stream)
        source.connect(analyser)
      }

      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder
      const audioChunks: BlobPart[] = []

      mediaRecorder.addEventListener('dataavailable', (event) => {
        audioChunks.push(event.data)
      })

      mediaRecorder.addEventListener('stop', () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' })
        setAudioBlob(audioBlob)
      })

      mediaRecorder.start()
      setIsRecording(true)
    } catch (error) {
      console.error('Error starting recording:', error)
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
    }
  }

  const playAudio = () => {
    if (audioBlob) {
      const audio = new Audio(URL.createObjectURL(audioBlob))
      audioRef.current = audio
      audio.play()
      setIsPlaying(true)
      audio.onended = () => setIsPlaying(false)
    }
  }

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      setIsPlaying(false)
    }
  }

  const drawWaveform = () => {
    if (canvasRef.current && analyser && dataArray) {
      const canvas = canvasRef.current
      const canvasCtx = canvas.getContext('2d')
      if (!canvasCtx) return

      const draw = () => {
        const WIDTH = canvas.width
        const HEIGHT = canvas.height

        analyser.getByteTimeDomainData(dataArray)

        canvasCtx.fillStyle = 'rgb(255, 255, 255)'
        canvasCtx.fillRect(0, 0, WIDTH, HEIGHT)
        canvasCtx.lineWidth = 2
        canvasCtx.strokeStyle = 'rgb(0, 0, 0)'
        canvasCtx.beginPath()

        const sliceWidth = (WIDTH * 1.0) / dataArray.length
        let x = 0

        for (let i = 0; i < dataArray.length; i++) {
          const v = dataArray[i] / 128.0
          const y = (v * HEIGHT) / 2

          if (i === 0) {
            canvasCtx.moveTo(x, y)
          } else {
            canvasCtx.lineTo(x, y)
          }

          x += sliceWidth
        }

        canvasCtx.lineTo(canvas.width, canvas.height / 2)
        canvasCtx.stroke()

        if (isRecording) {
          requestAnimationFrame(draw)
        }
      }

      draw()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white p-6 rounded-lg max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Audio Recording</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            <X size={24} />
          </button>
        </div>
        <div className="mb-4">
          <canvas ref={canvasRef} width="320" height="100" className="w-full bg-gray-100 rounded"></canvas>
        </div>
        <div className="flex justify-center space-x-4">
          {!audioBlob ? (
            <>
              {!isRecording ? (
                <button
                  onClick={startRecording}
                  className="p-2 rounded-full bg-red-500 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400"
                >
                  <Mic size={24} />
                </button>
              ) : (
                <button
                  onClick={stopRecording}
                  className="p-2 rounded-full bg-gray-500 text-white hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400"
                >
                  <Square size={24} />
                </button>
              )}
            </>
          ) : (
            <>
              <button
                onClick={isPlaying ? stopAudio : playAudio}
                className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <Play size={24} />
              </button>
              <button
                onClick={() => onSend(audioBlob)}
                className="p-2 rounded-full bg-green-500 text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                <Send size={24} />
              </button>
            </>
          )}
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-gray-300 text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

export default AudioRecordingModal
